interface Option {
  value: string;
  label: string;
}

interface FieldProps {
  label: string;
  name: string;
  variant?: "text" | "email" | "tel" | "number" | "select" | "textarea" | "file";
  required?: boolean;
  placeholder?: string;
  options?: Option[];
  rows?: number;
  maxLength?: number;
  className?: string;
  accept?: string;
  hint?: string;
  autoComplete?: string;
  min?: number;
  max?: number;
}

const inputClasses =
  "w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-200";

export default function Field({
  label,
  name,
  variant = "text",
  required,
  placeholder,
  options = [],
  rows = 4,
  maxLength,
  className = "",
  accept,
  hint,
  autoComplete,
  min,
  max,
}: FieldProps) {
  const labelEl = (
    <label htmlFor={name} className="mb-1.5 block font-label text-xs font-semibold uppercase tracking-[0.1em] text-foreground-700">
      {label}
      {required ? <span className="ml-1 text-primary-600">*</span> : null}
    </label>
  );

  return (
    <div className={className}>
      {labelEl}
      {variant === "select" ? (
        <select id={name} name={name} required={required} defaultValue="" className={inputClasses}>
          <option value="" disabled>
            Select an option
          </option>
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      ) : null}

      {variant === "textarea" ? (
        <textarea
          id={name}
          name={name}
          required={required}
          rows={rows}
          maxLength={maxLength}
          placeholder={placeholder}
          className={`${inputClasses} resize-none`}
        />
      ) : null}

      {variant === "file" ? (
        <input
          id={name}
          name={name}
          type="file"
          accept={accept}
          className="w-full cursor-pointer rounded-md border border-dashed border-background-300 bg-background-50 px-3.5 py-2.5 text-xs text-foreground-600 file:mr-3 file:cursor-pointer file:rounded-md file:border-0 file:bg-primary-100 file:px-3 file:py-1.5 file:font-label file:text-xs file:font-semibold file:text-primary-800"
        />
      ) : null}

      {variant !== "select" && variant !== "textarea" && variant !== "file" ? (
        <input
          id={name}
          name={name}
          type={variant}
          required={required}
          placeholder={placeholder}
          maxLength={maxLength}
          autoComplete={autoComplete}
          min={min}
          max={max}
          className={inputClasses}
        />
      ) : null}

      {hint ? <p className="mt-1.5 text-[11px] text-foreground-500">{hint}</p> : null}
    </div>
  );
}